import React, { useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'

import Button, { ButtonColor } from '@/components/view/atoms/button/Button'
import Main, { MainLayoutType } from '@/components/view/layouts/page/Main'
import PageTitle from '@/components/view/molecules/title/PageTitle'

import { fetchRest } from '@/common/api/fetcher'

// 共通スタイル（SmartHub デザイントークン）
const CARD = 'rounded-xl border border-secondary bg-surface shadow-area'
const TH = 'text-secondary border-b border-secondary px-2.5 py-2 text-left text-[11px] font-semibold whitespace-nowrap'
const TD = 'text-primary px-2.5 py-2 align-middle'

/** SEO対応状況の商品一覧（backend/routers/ugc_seo.py の seo_product_list） */
interface SeoProductRow {
  product_external_id: string
  review_count: number
  avg_rating: number
  schema_valid: boolean
}

/** 構造化データのプレビュー（同 seo_preview） */
interface SeoPreview {
  product_id: string
  avg_rating: number
  review_count: number
  star_distribution: Record<string, number>
  schema_valid: boolean
  google_snippet_preview: string | null
  recent_reviews: {
    reviewer_name: string | null
    rating: number | null
    title: string | null
    body: string
    created_at: string | null
  }[]
}

const PER_PAGE = 20

export default function UGCSEODashboard() {
  const { siteId } = useParams()
  const [products, setProducts] = useState<SeoProductRow[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [selectedProduct, setSelectedProduct] = useState<string | null>(null)
  const [preview, setPreview] = useState<SeoPreview | null>(null)
  const [loading, setLoading] = useState(true)
  // 最後に選んだ商品。古いプレビュー応答で上書きしないための照合用
  const latestPreview = useRef<string | null>(null)

  const handlePreview = (productId: string) => {
    setSelectedProduct(productId)
    latestPreview.current = productId
    fetchRest<SeoPreview>(
      `/api/sites/${siteId}/ugc/seo/preview?product_id=${encodeURIComponent(productId)}`
    )
      // 素早く切り替えたとき、先に投げた古い応答で上書きしない
      .then(p => setPreview(prev => (latestPreview.current === productId ? p : prev)))
      .catch(() => {})
  }

  useEffect(() => {
    setLoading(true)
    fetchRest<{ products?: SeoProductRow[]; total?: number }>(
      `/api/sites/${siteId}/ugc/seo/products?page=${page}&per_page=${PER_PAGE}`
    )
      .then(d => {
        const rows = d.products || []
        setProducts(rows)
        setTotal(d.total || 0)
        // 2026-09-29 追加：開いた時点（ページ送り時も）で先頭の商品のプレビューを出しておく。
        // 何も選ばれていないと右側が空で、この画面で何が見られるのか伝わらない
        if (rows.length > 0) handlePreview(rows[0].product_external_id)
        else { setSelectedProduct(null); setPreview(null) }
      })
      .catch(() => {})
      .finally(() => setLoading(false))
    // handlePreview は siteId にだけ依存する。依存に入れると毎描画で取り直しになる
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteId, page])

  const totalPages = Math.max(Math.ceil(total / PER_PAGE), 1)

  return (
    <Main type={MainLayoutType.FULL}>
      <PageTitle
        title='🔍 SEOダッシュボード'
        description='承認済みレビューをもとにした構造化データ（リッチスニペット）の状況を確認します'
      />

      {loading ? (
        <div className='text-secondary py-16 text-center text-sm'>読み込み中...</div>
      ) : (
        <>
          {/* KPI */}
          <div className='mb-4 grid grid-cols-3 gap-3'>
            <KpiCard label='レビュー対応商品' value={total} />
            <KpiCard label='スニペット有効' value={products.filter(p => p.schema_valid).length} valueClass='text-success' />
            <KpiCard
              label='平均評価'
              value={`★ ${products.length > 0 ? (products.reduce((s, p) => s + p.avg_rating, 0) / products.length).toFixed(1) : '-'}`}
              valueClass='text-warning'
            />
          </div>

          <div className={`grid gap-4 ${selectedProduct ? 'grid-cols-1 lg:grid-cols-2' : 'grid-cols-1'}`}>
            {/* 商品一覧 */}
            <div className={`${CARD} overflow-hidden p-0`}>
              <div className='overflow-x-auto'>
                <table className='w-full border-collapse text-xs'>
                  <thead>
                    <tr className='bg-surface-secondary'>
                      <th className={TH}>商品ID</th>
                      <th className={TH}>レビュー数</th>
                      <th className={TH}>平均評価</th>
                      <th className={TH}>スニペット</th>
                      <th className={TH}>操作</th>
                    </tr>
                  </thead>
                  <tbody>
                    {products.length === 0 ? (
                      <tr><td colSpan={5} className='text-tertiary p-10 text-center'>承認済みレビューのある商品がありません</td></tr>
                    ) : products.map(p => (
                      <tr
                        key={p.product_external_id}
                        className={`border-b border-secondary ${selectedProduct === p.product_external_id ? 'bg-surface-emphasis' : ''}`}
                      >
                        <td className={`${TD} font-semibold`}>{p.product_external_id}</td>
                        <td className={TD}>{p.review_count}</td>
                        <td className={TD}>
                          <span className='text-warning'>★ {p.avg_rating}</span>
                        </td>
                        <td className={TD}>
                          <span className={`rounded-[10px] px-2 py-0.5 text-[10px] font-semibold ${
                            p.schema_valid ? 'bg-surface-success text-success' : 'bg-surface-secondary text-secondary'
                          }`}>{p.schema_valid ? '有効' : '無効'}</span>
                        </td>
                        <td className={TD}>
                          <button onClick={() => handlePreview(p.product_external_id)} className='text-link cursor-pointer text-[11px] font-semibold'>プレビュー</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* 20件ずつしか取っていないのに送りが無く、21件目以降を見る手段が無かった */}
              {totalPages > 1 && (
                <div className='flex items-center justify-center gap-2 border-t border-secondary px-4 py-3'>
                  <Button color={ButtonColor.SECONDARY} disabled={page <= 1} onClick={() => setPage(p => p - 1)}>←</Button>
                  <span className='text-secondary px-2 text-[11px]'>{page} / {totalPages}</span>
                  <Button color={ButtonColor.SECONDARY} disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>→</Button>
                </div>
              )}
            </div>

            {/* SEOプレビュー */}
            {preview && (
              <div className='flex flex-col gap-4'>
                {/* Googleスニペットプレビュー */}
                <div className={`${CARD} p-5`}>
                  <h3 className='text-primary m-0 text-sm font-bold'>Googleリッチスニペット プレビュー</h3>
                  {preview.google_snippet_preview ? (
                    <div className='bg-surface-secondary mt-3 rounded-lg border border-secondary p-4'>
                      <div className='text-link mb-1 text-lg font-normal'>商品名 - ブランドサイト</div>
                      <div className='text-success mb-1 text-xs'>https://example.com/products/{preview.product_id}</div>
                      <div className='text-warning mb-1 text-[13px]'>{preview.google_snippet_preview}</div>
                      <div className='text-secondary text-[13px]'>商品の説明文がここに表示されます...</div>
                    </div>
                  ) : (
                    <p className='text-tertiary mt-3 text-[13px]'>レビューがないためスニペットは表示されません</p>
                  )}
                </div>

                {/* 星分布 */}
                <div className={`${CARD} p-5`}>
                  <h3 className='text-primary m-0 text-sm font-bold'>評価分布</h3>
                  <div className='mt-3 flex flex-col gap-1'>
                    {[5, 4, 3, 2, 1].map(star => (
                      <div key={star} className='flex items-center gap-2'>
                        <span className='text-warning w-5 text-xs'>{star}★</span>
                        <div className='bg-surface-secondary h-2.5 flex-1 overflow-hidden rounded-full'>
                          <div
                            className={`h-full rounded-full ${star >= 4 ? 'bg-fill-success' : star === 3 ? 'bg-fill-warning' : 'bg-fill-critical'}`}
                            style={{ width: `${preview.review_count > 0 ? (preview.star_distribution?.[star] || 0) / preview.review_count * 100 : 0}%` }}
                          />
                        </div>
                        <span className='text-secondary w-6 text-right text-[11px] font-semibold'>{preview.star_distribution?.[star] || 0}</span>
                      </div>
                    ))}
                  </div>
                  <div className='text-secondary mt-2.5 flex justify-between text-xs'>
                    <span>平均: <strong className='text-warning'>★ {preview.avg_rating}</strong></span>
                    <span>レビュー数: <strong className='text-primary'>{preview.review_count}</strong></span>
                  </div>
                </div>

                {/* 最新レビュー */}
                {(preview.recent_reviews?.length ?? 0) > 0 && (
                  <div className={`${CARD} p-5`}>
                    <h3 className='text-primary m-0 text-sm font-bold'>最新レビュー（JSON-LDに含まれる）</h3>
                    <div className='mt-2.5 flex flex-col gap-2'>
                      {(preview.recent_reviews ?? []).map((rv, i) => (
                        <div key={i} className='bg-surface-secondary rounded-lg border border-secondary p-2.5'>
                          <div className='mb-1 flex items-center gap-1.5'>
                            <span className='text-primary text-xs font-semibold'>{rv.reviewer_name || '匿名'}</span>
                            <span className='text-warning text-xs'>{'★'.repeat(rv.rating || 0)}</span>
                            <span className='text-tertiary ml-auto text-[10px]'>{rv.created_at?.slice(0, 10)}</span>
                          </div>
                          {rv.title && <div className='text-secondary mb-0.5 text-[11px] font-semibold'>{rv.title}</div>}
                          <p className='text-tertiary m-0 text-[11px] leading-normal'>{rv.body}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </>
      )}
    </Main>
  )
}

interface KpiCardProps {
  label: string
  value: string | number
  valueClass?: string
}

function KpiCard({ label, value, valueClass = 'text-primary' }: KpiCardProps) {
  return (
    <div className={`${CARD} px-4 py-3.5`}>
      <div className='text-secondary mb-1 text-xs'>{label}</div>
      <div className={`text-2xl font-bold ${valueClass}`}>
        {typeof value === 'number' ? value.toLocaleString() : value}
      </div>
    </div>
  )
}
